import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Service pour gérer la progression Roulette à Rythmes dans Firestore
 * Chaque utilisateur a sa propre progression stockée dans users/{userId}/rouletteProgress
 */
class RouletteProgressService {

  /**
   * Récupère la progression Roulette d'un utilisateur depuis Firestore
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<object>} - Progression par palier
   */
  async getProgress(userId) {
    try {
      const progressRef = doc(db, `users/${userId}/rouletteProgress`, 'data');
      const progressSnap = await getDoc(progressRef);

      if (progressSnap.exists()) {
        return progressSnap.data().progress || this.initializeProgress();
      }

      return this.initializeProgress();
    } catch (error) {
      console.error('❌ Erreur récupération progression Roulette:', error);
      return this.initializeProgress();
    }
  }

  /**
   * Initialise une progression vierge pour les 12 paliers
   * @returns {object} - Progression vide
   */
  initializeProgress() {
    const progress = {};
    for (let i = 1; i <= 12; i++) {
      progress[i] = {
        completed: []
      };
    }
    return progress;
  }

  /**
   * Sauvegarde la progression complète dans Firestore
   * @param {string} userId - ID de l'utilisateur
   * @param {object} progress - Progression à sauvegarder
   */
  async saveProgress(userId, progress) {
    try {
      const progressRef = doc(db, `users/${userId}/rouletteProgress`, 'data');
      await setDoc(progressRef, {
        progress,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('❌ Erreur sauvegarde progression Roulette:', error);
      throw error;
    }
  }

  /**
   * Enregistre un rythme complété pour un palier
   * @param {string} userId - ID de l'utilisateur
   * @param {number} tierId - ID du palier
   * @param {string} seed - Identifiant unique du rythme
   * @param {number} time - Temps en secondes
   * @param {number} errors - Nombre d'erreurs
   * @param {number|null} averageTempo - Tempo moyen en BPM (pour Métronome Challenge)
   * @returns {Promise<object>} - Progression mise à jour
   */
  async saveScore(userId, tierId, seed, time, errors, averageTempo = null) {
    try {
      const progress = await this.getProgress(userId);

      if (!progress[tierId]) {
        progress[tierId] = { completed: [] };
      }

      const score = time + (errors * 2); // Pénalité : +2 secondes par erreur
      const timestamp = Date.now();

      // Vérifier si ce rythme a déjà été joué
      const existingIndex = progress[tierId].completed.findIndex(c => c.seed === seed);

      const entry = {
        seed,
        time,
        errors,
        score,
        timestamp,
        averageTempo  // NOUVEAU: tempo moyen pour Métronome Challenge
      };

      if (existingIndex >= 0) {
        // Garder le meilleur score uniquement
        const existingScore = progress[tierId].completed[existingIndex].score;
        if (score < existingScore) {
          progress[tierId].completed[existingIndex] = entry;
        }
      } else {
        // Nouveau rythme
        progress[tierId].completed.push(entry);
      }

      // Sauvegarder dans Firestore
      await this.saveProgress(userId, progress);

      return progress;
    } catch (error) {
      console.error('❌ Erreur enregistrement score Roulette:', error);
      throw error;
    }
  }

  /**
   * Récupère les stats d'un palier
   * @param {string} userId - ID de l'utilisateur
   * @param {number} tierId - ID du palier
   * @returns {Promise<object>} - { completedCount, totalScore, totalErrors }
   */
  async getTierProgress(userId, tierId) {
    try {
      const progress = await this.getProgress(userId);
      const tierData = progress[tierId] || { completed: [] };

      const completedCount = tierData.completed.length;
      const totalScore = tierData.completed.reduce((sum, c) => sum + c.score, 0);
      const totalErrors = tierData.completed.reduce((sum, c) => sum + c.errors, 0);

      return {
        completedCount,
        totalScore,
        totalErrors,
        completed: tierData.completed
      };
    } catch (error) {
      console.error('❌ Erreur récupération stats palier:', error);
      return {
        completedCount: 0,
        totalScore: 0,
        totalErrors: 0,
        completed: []
      };
    }
  }

  /**
   * Pré-débloque les paliers 1 à maxTierId-1 pour rendre le palier maxTierId accessible.
   * Ajoute un flag preUnlocked: true (sans créer de faux rythmes joués).
   * @param {string} userId - ID de l'utilisateur
   * @param {number} maxTierId - Palier max à rendre accessible (inclus)
   * @returns {Promise<object>} Progression mise à jour
   */
  async preUnlockTiersUpTo(userId, maxTierId) {
    try {
      const progress = await this.getProgress(userId);
      for (let i = 1; i < maxTierId; i++) {
        if (!progress[i]) progress[i] = { completed: [] };
        progress[i].preUnlocked = true;
      }
      await this.saveProgress(userId, progress);
      console.log(`✅ Paliers Roulette 1-${maxTierId - 1} pré-débloqués (accès jusqu'au palier ${maxTierId})`);
      return progress;
    } catch (error) {
      console.error('❌ Erreur pré-déblocage paliers Roulette:', error);
      throw error;
    }
  }

  /**
   * Migre les données localStorage vers Firestore pour un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {object} localProgress - Progression depuis localStorage
   */
  async migrateFromLocalStorage(userId, localProgress) {
    try {
      console.log('🔄 Migration progression Roulette vers Firestore pour user:', userId);

      // Récupérer la progression existante dans Firestore
      const firestoreProgress = await this.getProgress(userId);

      // Fusionner les données (localStorage a priorité si meilleur score)
      for (let tierId = 1; tierId <= 12; tierId++) {
        const localTier = localProgress[tierId] || { completed: [] };
        const firestoreTier = firestoreProgress[tierId] || { completed: [] };

        // Pour chaque rythme complété localement
        localTier.completed.forEach(localEntry => {
          const existingIndex = firestoreTier.completed.findIndex(
            c => c.seed === localEntry.seed
          );

          if (existingIndex >= 0) {
            // Garder le meilleur score
            if (localEntry.score < firestoreTier.completed[existingIndex].score) {
              firestoreTier.completed[existingIndex] = localEntry;
            }
          } else {
            // Ajouter le nouveau rythme
            firestoreTier.completed.push(localEntry);
          }
        });

        firestoreProgress[tierId] = firestoreTier;
      }

      // Sauvegarder la progression fusionnée
      await this.saveProgress(userId, firestoreProgress);

      console.log('✅ Migration Roulette terminée');
      return firestoreProgress;
    } catch (error) {
      console.error('❌ Erreur migration Roulette:', error);
      throw error;
    }
  }
}

export default new RouletteProgressService();
